import { Button, IconButton, Menu, Tooltip } from "@itk/mui-components";
import { Accordion, AccordionDetails, AccordionSummary, Box, Checkbox, MenuItem, Stack, Typography, useTheme, Zoom } from "@mui/material";
import { ChipList, DekoxIcon } from "src/components";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { MouseEvent, useState } from "react";
import { useConfiguratorPageT } from "src/translation-hooks/configurator-page";
import { connect } from "react-redux";
import { configuratorActions } from "src/pages/configurator/configurator-store";
import { ProductConfiguration } from "../product-configuration";
import { useNavigate, useParams } from "react-router";
import { ROUTES_URL } from "src/utils/constants";
import { RootState } from "src/store";
import { addEditConfigurationDialogActions } from "../../../add-edit-configuration-dialog/add-edit-configuration-dialog-store";
import { Model_ConfigurationsGet } from "src/backend/openapi/v1";
import { applicationActions } from "src/components/application-store";
import { getLabelsForEntity, getProjectLineItemsHierarchy } from "src/pages/configurator/configurator-utils";

const mapStateToProps = (state: RootState) => ({
  selectedConfigurations: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.selectedConfigurations,
  labels: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.labels
});

const ownActions = {
  setAddEditConfigurationDialog: addEditConfigurationDialogActions.setAddEditConfigurationDialog,
  setFormData: addEditConfigurationDialogActions.setFormData,
  setSelectedConfigurations: configuratorActions.setSelectedConfigurations,
  deleteConfigurationsRequest: configuratorActions.deleteConfigurationsRequest,
  copyProjectLineItemsRequest: configuratorActions.copyProjectLineItemsRequest,
  getConfigurationsRequest: configuratorActions.getConfigurationsRequest,
  getLabelsRequest: configuratorActions.getLabelsRequest,
  setWarningAction: applicationActions.setWarningAction
};

type Props = typeof ownActions &
  ReturnType<typeof mapStateToProps> & {
    configuration: Model_ConfigurationsGet["configurations"][number];
  };

const ConfigurationBase = (props: Props) => {
  const {
    configuration,
    selectedConfigurations,
    setSelectedConfigurations,
    setAddEditConfigurationDialog,
    setFormData,
    setWarningAction,
    deleteConfigurationsRequest,
    getConfigurationsRequest,
    copyProjectLineItemsRequest,
    getLabelsRequest,
    labels
  } = props;

  const theme = useTheme();
  const navigate = useNavigate();
  const configuratorPageT = useConfiguratorPageT();
  const genericTranslationsT = useGenericTranslationsT();

  const { projectId = "" } = useParams<{ projectId: string }>();

  const [isExpanded, setIsExpanded] = useState(false);
  const [copyBtnAnchor, setCopyBtnAnchor] = useState<HTMLButtonElement | null>(null);

  const firstProjectLineItemId = configuration.projectLineItems?.find(x => x.parentProjectLineItemId === null)?.projectLineItemId;

  const handleEdit = (e: MouseEvent) => {
    e.stopPropagation();
    navigate(ROUTES_URL.CONFIGURE_PRODUCT.build(projectId, configuration.configurationId?.toString() || ""));
  };

  const handleCopy = (blankValues: boolean) => {
    if (!configuration.configurationId || !firstProjectLineItemId) return;

    copyProjectLineItemsRequest(blankValues, { projectLineItemIds: [firstProjectLineItemId] }, isSuccess => {
      if (isSuccess) {
        getConfigurationsRequest(projectId);
        getLabelsRequest(projectId);
      }
    });
  };

  const handleDelete = (e: MouseEvent) => {
    e.stopPropagation();
    setWarningAction({
      confirm: () => {
        if (!configuration.configurationId) return;
        deleteConfigurationsRequest({ configurationIds: [configuration.configurationId] }, isSuccess => {
          if (isSuccess) {
            getConfigurationsRequest(projectId);
          }
        });
        setWarningAction(undefined);
      },
      dialogProps: {
        message: configuratorPageT.areYouSureYouWantToDeleteThisProductConfiguration,
        confirmButtonTranslation: {
          t: genericTranslationsT,
          k: genericTranslationsK,
          field: "delete"
        },
        messageIcon: <DekoxIcon icon="delete" />
      },
      variant: "confirmCancel"
    });
  };

  const lastProjectLineItem = getProjectLineItemsHierarchy(configuration.projectLineItems || []).last();

  const open = Boolean(copyBtnAnchor);
  const id = open ? "copy-btn-popover" : undefined;

  return (
    <>
      <Accordion
        expanded={isExpanded}
        onChange={(_, expanded) => setIsExpanded(expanded)}
        sx={{
          boxShadow: "none",
          border: `1px solid ${theme.palette.customColors.grey}`,
          borderRadius: 1,
          overflow: "hidden",
          "&.Mui-expanded": {
            margin: 0,
            ".MuiSvgIcon-root[data-testid='KeyboardArrowDownIcon']": {
              transform: "rotate(180deg)"
            }
          },
          ":last-of-type": {
            borderRadius: 1
          }
        }}
      >
        <AccordionSummary
          sx={{
            overflow: "hidden",
            minHeight: 0,
            ".MuiAccordionSummary-content": {
              overflow: "hidden",
              my: 0
            }
          }}
        >
          <Stack width="100%" direction="row" justifyContent="space-between" alignItems="center" overflow="hidden">
            <Stack flex={1} direction="row" alignItems="center" overflow="hidden">
              <Checkbox
                size="small"
                checked={!!selectedConfigurations.find(x => x === configuration.configurationId)}
                onClick={e => e.stopPropagation()}
                onChange={(e, checked) => {
                  e.stopPropagation();
                  if (checked) {
                    setSelectedConfigurations([...selectedConfigurations, configuration.configurationId || ""]);
                  } else {
                    setSelectedConfigurations(selectedConfigurations.filter(x => x !== configuration.configurationId));
                  }
                }}
              />
              <DekoxIcon icon="arrow-down" />
              <Typography variant="body2" fontWeight="500" ml={1}>
                {configuration.configurationName}
              </Typography>
              <Typography variant="caption" ml={3} mr={2}>
                {configuration.projectLineItems?.length} {configuratorPageT.product}
              </Typography>
              {labels && configuration.configurationId && <ChipList labels={getLabelsForEntity(labels, configuration.configurationId).map(label => label.shortname)} />}
            </Stack>

            <Stack direction="row" alignItems="center">
              {!isExpanded && configuration.missingInformation && configuration.missingInformation?.length > 0 && (
                <Tooltip title={configuratorPageT.productIsMissingInformation} arrow placement="bottom" slots={{ transition: Zoom }}>
                  <DekoxIcon icon="info" fontSize="small" color="warning" sx={{ mx: 1 }} />
                </Tooltip>
              )}
              <IconButton
                k={genericTranslationsK.copy}
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  setCopyBtnAnchor(e.currentTarget);
                }}
                sx={{ color: theme.palette.customColors.darkGrey }}
              >
                <DekoxIcon icon="copy" fontSize="small" />
              </IconButton>

              <IconButton k={genericTranslationsK.edit} size="small" onClick={handleEdit} sx={{ color: theme.palette.customColors.darkGrey }}>
                <DekoxIcon icon="edit" fontSize="small" />
              </IconButton>

              <IconButton k={genericTranslationsK.delete} size="small" onClick={handleDelete} sx={{ color: theme.palette.customColors.darkGrey }}>
                <DekoxIcon icon="delete" fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Stack gap={1.4}>
            {getProjectLineItemsHierarchy(configuration.projectLineItems || []).map(item => {
              return <ProductConfiguration key={item.projectLineItemId} missingInformation={configuration.missingInformation} projectLineItem={item} />;
            })}
            {lastProjectLineItem?.nextProductTypeId && (
              <Box>
                <Button
                  size="small"
                  t={genericTranslationsT}
                  k={genericTranslationsK}
                  field="dynamicValue"
                  tArgs={[lastProjectLineItem?.nextProductTypeName || ""]}
                  variant="contained"
                  startIcon={<DekoxIcon icon="add" />}
                  onClick={() => {
                    if (!lastProjectLineItem?.projectLineItemId || !lastProjectLineItem?.systemPackageId || !lastProjectLineItem?.nextProductTypeId) return;
                    setFormData({
                      productsFilter: {
                        parentProjectLineItemId: lastProjectLineItem.projectLineItemId,
                        systemPackageId: lastProjectLineItem.systemPackageId,
                        productTypes: [lastProjectLineItem.nextProductTypeId]
                      }
                    });
                    setAddEditConfigurationDialog({ step: "SELECT PRODUCT" });
                  }}
                />
              </Box>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>
      <Menu id={id} anchorEl={copyBtnAnchor} open={open} onClose={() => setCopyBtnAnchor(null)}>
        <MenuItem sx={{ "&:hover": { backgroundColor: theme.palette.customColors.grey } }} onClick={() => handleCopy(true)}>
          {genericTranslationsT.copyBlank}
        </MenuItem>
        <MenuItem sx={{ "&:hover": { backgroundColor: theme.palette.customColors.grey } }} onClick={() => handleCopy(false)}>
          {genericTranslationsT.copyWithValues}
        </MenuItem>
      </Menu>
    </>
  );
};

export const Configuration = connect(mapStateToProps, ownActions)(ConfigurationBase);
